import React, { useState } from "react";
import { Plus } from "lucide-react";

import PhaseItem from "./PhaseItem";
import PhaseFormModal from "./PhaseFormModal";

import phaseService from "../../services/phaseService";

/* ===================================================== */
/* PHASES TAB                                            */
/* ===================================================== */

export default function PhasesTab({ project, setProject, isPro }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPhase, setEditingPhase] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const phases = Array.isArray(project?.phases)
    ? project.phases
    : [];

  /* ===================================================== */
  /* CREATE / UPDATE PHASE                                 */
  /* ===================================================== */

  const handleSavePhase = async (payload) => {
    try {
      setLoading(true);
      setError(null);

      let saved;

      if (editingPhase?._id) {
        // 🚧 Backend not implemented yet
        throw new Error("Phase update not supported yet");
      } else {
        saved = await phaseService.createPhase(project._id, payload);

        setProject((prev) => ({
          ...prev,
          phases: [...(prev.phases || []), saved],
        }));
      }

      setModalOpen(false);
      setEditingPhase(null);

    } catch (err) {
      setError(err.message || "Failed to save phase");
    } finally {
      setLoading(false);
    }
  };

  /* ===================================================== */
  /* DELETE PHASE (LOCAL ONLY - TEMP)                      */
  /* ===================================================== */



const handleDeletePhase = async (phaseId) => {

  if (!window.confirm("Delete this phase?")) return;

  try {

    /* 🔥 REAL DELETE API */

    await phaseService.deletePhase(project._id, phaseId);

    /* ✅ UPDATE UI AFTER SUCCESS */

    setProject((prev) => ({

      ...prev,

      phases: prev.phases.filter((p) => p._id !== phaseId),

    }));

  } catch (err) {

    console.error("[Phase Delete Error]", err);

    alert(err.message || "Failed to delete phase");

  }

};

  /* ===================================================== */
  /* OPEN MODAL                                            */
  /* ===================================================== */

  const openCreate = () => {
    setEditingPhase(null);
    setModalOpen(true);
  };

  const openEdit = (phase) => {
    setEditingPhase(phase);
    setModalOpen(true);
  };

  /* ===================================================== */
  /* RENDER                                                */
  /* ===================================================== */

  return (
    <div className="space-y-4">

      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Project Phases</h2>
          <p className="text-xs text-gray-500">
            Organize your project into structured execution phases
          </p>
        </div>

        <button
          onClick={openCreate}
          className="flex items-center gap-1 rounded-lg bg-black px-3 py-2 text-sm text-white disabled:opacity-40"
        >
          <Plus className="w-4 h-4" />
          Add Phase
        </button>
      </div>

      {/* ERROR */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* EMPTY STATE */}
      {phases.length === 0 ? (
        <div className="rounded-xl border bg-white p-6 text-center text-sm text-gray-500">
          Break your project into clear execution phases to track progress better.
        </div>
      ) : (
        <ul className="divide-y rounded-xl border bg-white">
          {phases.map((phase, index) => (
            <PhaseItem
              key={phase._id || `phase-${index}`}
              phase={phase}
              isPro={isPro}
              onEdit={() => openEdit(phase)}
              onDelete={() => handleDeletePhase(phase._id)}
            />
          ))}
        </ul>
      )}

      {/* MODAL */}
      <PhaseFormModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingPhase(null);
        }}
        onSave={handleSavePhase}
        phase={editingPhase}
        project={project}
        isPro={isPro}
        loading={loading}
      />
    </div>
  );
}